import { useEffect, useRef } from 'react';
import { PurchaseEventService } from 'src/services/purchase-event/purchase-event.service';
import { Logger } from 'src/utils/logger';

const STORAGE_KEY = 'pending_purchase_events_processed';

/**
 * Hook to process pending purchase events on mount
 * Should be called once per session when user is authenticated
 * Uses sessionStorage to prevent re-processing on component remounts
 */
export const usePendingPurchaseEvents = (isAuthenticated: boolean) => {
  const hasScheduled = useRef(false);

  useEffect(() => {
    // Check sessionStorage to handle component remounts across navigations
    const alreadyProcessedInSession = sessionStorage.getItem(STORAGE_KEY) === 'true';

    if (!isAuthenticated || hasScheduled.current || alreadyProcessedInSession) {
      return;
    }

    // Mark as scheduled immediately (ref persists across re-renders, won't re-schedule)
    hasScheduled.current = true;

    // Process with delay to ensure analytics scripts are loaded
    // GTM, Meta Pixel, and PostHog scripts are loaded asynchronously via <script> tags
    // 1000ms provides reasonable buffer for most network conditions while keeping UX responsive
    // If scripts aren't loaded by then, they'll be treated as unavailable (logged as warnings, not errors)
    const timeoutId = setTimeout(async () => {
      // Persist to sessionStorage only when actually executing
      sessionStorage.setItem(STORAGE_KEY, 'true');

      try {
        Logger.info(
          'Starting pending purchase events processing',
          'usePendingPurchaseEvents',
        );
        await PurchaseEventService.processPendingEvents();
      } catch (error) {
        Logger.error('Failed to process pending purchase events', 'usePendingPurchaseEvents', error);
      }
    }, 1000);

    // Don't clear timeout on cleanup - let it complete even if component re-renders
    // The ref guard prevents duplicate scheduling
  }, [isAuthenticated]);
};
